import React, { useContext, useEffect, useRef, useState } from "react";
import { Option, some, none, fold } from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/pipeable";
import { CancelTokenSource } from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import { CalculatorStepsContext } from "../Store/CalculatorStepsContext";
import { CalculatorResultsContext } from "../Store/CalculatorResultsContext";
import { ExportPDFSummaryInterface } from "../../model/ExportPDFSummaryFields";
import Form from "../UI/Form/Form";
import InputField from "../UI/InputField/InputField";
import ButtonField from "../UI/ButtonField/ButtonField";
import "./ExportPDFSummary.scss";
import {
  EXPORT_PDF_SUMMARY_SUBTITLE,
  CLIENT_NAME_VALIDATION_MESSAGE,
  FINANCIAL_PROFESSIONAL_NAME_VALIDATION_MESSAGE,
} from "../../shared/constants";
import { generatePDFExport } from "../../components/CalculatorResults/ExportPDF";
import { createCancelTokenSource } from "../../api/CalculatorApi";

interface ExportPDFSummaryProps {
  onClickHideModalHandler: () => void;
  onFocusExportBtnHandler: () => void;
  onKeyUpBtnHandler: () => void;
}

const ExportPDFSummary: React.FC<ExportPDFSummaryProps> = (props) => {
  const { onClickHideModalHandler, onFocusExportBtnHandler, onKeyUpBtnHandler } = props;
  const resultsStore = useContext(CalculatorResultsContext);
  const { steps, setSteps } = useContext(CalculatorStepsContext);
  const [cancelTokenSource, setCancelTokenSource] =
    useState<Option<CancelTokenSource>>(none);
  const [isClientNameValid, setIsClientNameValid] = useState(true);
  const [
    isFinancialProfessionalNameValid,
    setIsFinancialProfessionalNameValid,
  ] = useState(true);
  const [autoDownload, setAutoDownload] = useState(false);
  const autoDownloadRef = useRef<HTMLAnchorElement>(null);
  const clientNameRef = useRef<HTMLInputElement>(null);
  const financialProfessionalNameRef = useRef<HTMLInputElement>(null);

  const createInitialFormData = (): ExportPDFSummaryInterface => {
    return {
      clientName: steps.inputs.exportReport.clientName.value,
      financialProfessionalName:
        steps.inputs.exportReport.financialProfessionalName.value,
    };
  };

  const formDataValue = createInitialFormData();

  const saveFormDataInState = () => {
    if (formDataValue) {
      const exportReport = steps.inputs.exportReport
        .withClientName(formDataValue.clientName)
        .withFinancialProfessionalName(formDataValue.financialProfessionalName);
      const newInputs = steps.inputs.withExportReport(exportReport);
      setSteps(steps.withInputs(newInputs));
    }
  };

  const resetFormData = (): ExportPDFSummaryInterface => {
    formDataValue.clientName = "";
    formDataValue.financialProfessionalName = "";
    saveFormDataInState();
    return formDataValue;
  };

  useEffect(() => {
    pipe(
      cancelTokenSource,
      fold(
        () => {
          return;
        },
        async (tokenSource) => {
          const inputs = steps.inputs;
          const config = steps.config;
          setAutoDownload(true);
          await generatePDFExport(inputs, tokenSource, config, resultsStore);
        }
      )
    );
  }, [cancelTokenSource]);

  useEffect(() => {
    if (autoDownload) {
      pipe(
        resultsStore.pdfUrl,
        fold(
          () => console.log("pdfUrl is none"),
          (_url) => {
            if (autoDownloadRef.current !== null) {
              autoDownloadRef.current.click();
              resetFormData();
              onClickHideModalHandler();
            }
          }
        )
      );
      setAutoDownload(false);
    }
  }, [resultsStore.pdfUrl]);

  const validateClientName = (formData: ExportPDFSummaryInterface) => {
    setIsClientNameValid(
      formData.clientName !== undefined && formData.clientName.length > 0
    );
    if (
      !(formData.clientName !== undefined && formData.clientName.length > 0)
    ) {
      clientNameRef.current && clientNameRef.current.focus();
    }
  };

  const validateFinancialProfessionalName = (
    formData: ExportPDFSummaryInterface
  ) => {
    setIsFinancialProfessionalNameValid(
      formData.financialProfessionalName !== undefined &&
        formData.financialProfessionalName.length > 0
    );
    if (
      !(
        formData.financialProfessionalName !== undefined &&
        formData.financialProfessionalName.length > 0
      ) &&
      formData.clientName !== undefined &&
      formData.clientName.length > 0
    ) {
      financialProfessionalNameRef.current &&
        financialProfessionalNameRef.current.focus();
    }
  };

  const validatePDFSummary = () => {
    validateClientName(formDataValue);
    validateFinancialProfessionalName(formDataValue);
    return (
      formDataValue.clientName &&
      formDataValue.clientName.length > 0 &&
      formDataValue.financialProfessionalName &&
      formDataValue.financialProfessionalName.length > 0
    );
  };

  const cancelPDFExportHandler = () => {
    pipe(
      cancelTokenSource,
      fold(
        () => console.log("no cancel token source"),
        (tokenSource) => tokenSource.cancel()
      )
    );
  };

  const onSubmitFormHandler = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validatePDFSummary()) {
      saveFormDataInState();
      setCancelTokenSource(some(createCancelTokenSource()));
    }
  };

  const onChangeClientName = (event: React.ChangeEvent<HTMLInputElement>) => {
    formDataValue.clientName = event.target.value;
    if (!isClientNameValid) {
      validateClientName(formDataValue);
    }
    saveFormDataInState();
  };

  const onChangeFinancialName = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    formDataValue.financialProfessionalName = event.target.value;
    if (!isFinancialProfessionalNameValid) {
      validateFinancialProfessionalName(formDataValue);
    }
    saveFormDataInState();
  };

  const onClickCancelButton = () => {
    if (resultsStore.exportingPdf) {
      cancelPDFExportHandler();
    }
    onClickHideModalHandler();
    onFocusExportBtnHandler();
  };

  const loadingIcon = (
    <FontAwesomeIcon
      data-testid="fa-loading-spinner-icon"
      icon={faSpinner}
      size="1x"
      color="white"
      spin
    />
  );

  const loadingSpinner = resultsStore.exportingPdf ? (
    <>
      Downloading <span className="icon">{loadingIcon}</span>
    </>
  ) : (
    "Download"
  );

  return (
    <div className="export-pdf-summary-wrapper" onKeyUp={onKeyUpBtnHandler}>
      <div className="export-pdf-summary-wrapper__description">
        <h2 id="heading-main" className="heading-main">Export PDF Summary</h2>
        <p>{EXPORT_PDF_SUMMARY_SUBTITLE}</p>
      </div>
      <div className="export-pdf-summary-wrapper__form">
        <Form
          name="export-pdf-summary-form"
          onSubmitHandler={onSubmitFormHandler}
        >
          <InputField
            type="text"
            id="client-name"
            name="client-name"
            className="input--primary"
            label="Client Name"
            isRequired={true}
            isInvalid={!isClientNameValid}
            reference={clientNameRef}
            onChangeHandler={onChangeClientName}
            validationMessages={CLIENT_NAME_VALIDATION_MESSAGE}
            value={steps.inputs.exportReport.clientName?.value || ""}
            isAutoFocus={true}
          />

          <InputField
            type="text"
            id="financial-professional-name"
            name="financial-professional-name"
            className="input--primary"
            label="Financial Professional Name"
            isRequired={true}
            isInvalid={!isFinancialProfessionalNameValid}
            reference={financialProfessionalNameRef}
            onChangeHandler={onChangeFinancialName}
            validationMessages={FINANCIAL_PROFESSIONAL_NAME_VALIDATION_MESSAGE}
            value={
              steps.inputs.exportReport.financialProfessionalName?.value || ""
            }
          />

          <div className="export-pdf-summary-wrapper__form--actions">
            <ButtonField
              type="button"
              name="cancel"
              title="Cancel"
              className="btn btn--secondary"
              onClickHandler={onClickCancelButton}
            />
            <ButtonField
              type="submit"
              name="download"
              className="btn btn--primary"
              isDisabled={resultsStore.exportingPdf}
              title={loadingSpinner}
            />
            <div className="auto-download">
              {pipe(
                resultsStore.pdfUrl,
                fold(
                  () => null,
                  (downloadUrl) => (
                    <a
                      href={downloadUrl}
                      target="_blank"
                      ref={autoDownloadRef}
                      rel="noopener noreferrer"
                      download="Prudential FlexGuard Calculator Report.pdf"
                    >
                      Fetch PDF
                    </a>
                  )
                )
              )}
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ExportPDFSummary;
